import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  User, 
  FileText, 
  Shield,
  Search,
  Filter,
  Eye,
  Pause,
  Play,
  UserX,
  Building2,
  ChevronRight,
  Wallet,
  Activity,
  Calendar,
  DollarSign,
  MapPin,
  Mail,
  Phone,
  CreditCard,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw
} from 'lucide-react';

const InvestorManagement = () => {
  const [activeView, setActiveView] = useState('list');
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showActionModal, setShowActionModal] = useState(null);

  // Mock data for approved investors
  const [investors, setInvestors] = useState([
    {
      id: 'INV-001',
      name: 'Ahmad bin Rahman',
      type: 'Individual',
      status: 'Active',
      email: 'ahmad.rahman@email.com',
      phone: '+60123456789',
      idNumber: '841225-14-5678',
      nationality: 'Malaysian',
      address: '123 Jalan Bukit Bintang, 55100 Kuala Lumpur',
      occupation: 'Software Engineer',
      approvalDate: '2024-01-13T16:30:00Z',
      lastActivity: '2024-01-15T10:45:00Z',
      wallets: [
        {
          address: '0x742dE5b2D72E5a85e9c1C...f7A2B8',
          status: 'Active',
          createdDate: '2024-01-14T09:00:00Z',
          holdings: [
            { bondName: 'Khazanah Conventional Bond 2026', tokens: 1000, value: 'MYR 100,000' }
          ]
        }
      ],
      transactions: [
        {
          id: 'TXN-001',
          date: '2024-01-15T10:45:00Z',
          type: 'Token Purchase',
          bond: 'Khazanah Conventional Bond 2026',
          amount: 'MYR 100,000',
          tokens: 1000,
          status: 'Completed'
        }
      ],
      riskRating: 'Low',
      kycStatus: 'Verified',
      lastComplianceReview: '2024-01-13T16:30:00Z'
    },
    {
      id: 'INV-002',
      name: 'Tech Solutions Sdn Bhd',
      type: 'Corporate',
      status: 'Active',
      email: 'compliance@techsolutions.com.my',
      phone: '+603-2123-4567',
      registrationNumber: '200501012345',
      address: '88 Jalan Sultan, 50000 Kuala Lumpur',
      businessNature: 'Information Technology Services',
      authorizedDirectors: [
        { name: 'Lim Wei Ming', designation: 'Managing Director' },
        { name: 'Sarah Abdullah', designation: 'Finance Director' }
      ],
      approvalDate: '2024-01-10T14:20:00Z',
      lastActivity: '2024-01-14T15:30:00Z',
      wallets: [
        {
          address: '0x852fF3c4D32A6b85e9c2D...g8B3C9',
          status: 'Active',
          createdDate: '2024-01-11T11:00:00Z',
          holdings: [
            { bondName: 'Green Infrastructure Bond 2024-A', tokens: 5000, value: 'MYR 500,000' },
            { bondName: 'Tech Development Sukuk', tokens: 2000, value: 'MYR 200,000' }
          ]
        }
      ],
      transactions: [
        {
          id: 'TXN-002',
          date: '2024-01-14T15:30:00Z',
          type: 'Token Purchase',
          bond: 'Green Infrastructure Bond 2024-A',
          amount: 'MYR 500,000',
          tokens: 5000,
          status: 'Completed'
        },
        {
          id: 'TXN-003',
          date: '2024-01-12T11:15:00Z',
          type: 'Token Purchase',
          bond: 'Tech Development Sukuk',
          amount: 'MYR 200,000',
          tokens: 2000,
          status: 'Completed'
        }
      ],
      riskRating: 'Medium',
      kycStatus: 'Verified',
      lastComplianceReview: '2024-01-10T14:20:00Z'
    },
    {
      id: 'INV-003',
      name: 'Jennifer Wong Su Ling',
      type: 'Individual',
      status: 'Suspended',
      email: 'jennifer.wong@email.com',
      phone: '+60198765432',
      idNumber: '900315-08-1234',
      nationality: 'Malaysian',
      address: '456 Persiaran KLCC, 50088 Kuala Lumpur',
      occupation: 'Investment Manager',
      approvalDate: '2024-01-08T11:45:00Z',
      lastActivity: '2024-01-10T09:20:00Z',
      wallets: [
        {
          address: '0x963eE4d5E43B7c96f0c3E...h9C4D0',
          status: 'Suspended',
          createdDate: '2024-01-09T13:30:00Z',
          holdings: [
            { bondName: 'Infrastructure Bond 2022-A', tokens: 1500, value: 'MYR 150,000' }
          ]
        }
      ],
      transactions: [
        {
          id: 'TXN-004',
          date: '2024-01-10T09:20:00Z',
          type: 'Token Purchase',
          bond: 'Infrastructure Bond 2022-A',
          amount: 'MYR 150,000',
          tokens: 1500,
          status: 'Completed'
        }
      ],
      riskRating: 'Medium',
      kycStatus: 'Under Review',
      lastComplianceReview: '2024-01-08T11:45:00Z',
      suspensionReason: 'Pending compliance verification'
    }
  ]);

  const filteredInvestors = investors.filter(investor => {
    const matchesSearch = investor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investor.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investor.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || investor.type === filterType;
    const matchesStatus = filterStatus === 'all' || investor.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusClasses = {
      Active: 'bg-green-100 text-green-800',
      Suspended: 'bg-yellow-100 text-yellow-800',
      Deactivated: 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active':
        return <CheckCircle className="h-4 w-4" />;
      case 'Suspended':
        return <Pause className="h-4 w-4" />;
      case 'Deactivated':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getRiskBadge = (riskRating) => {
    const riskClasses = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-red-100 text-red-800'
    };
    return riskClasses[riskRating] || 'bg-gray-100 text-gray-800';
  };

  const getKycStatusBadge = (status) => {
    const statusClasses = {
      Verified: 'bg-green-100 text-green-800',
      'Under Review': 'bg-yellow-100 text-yellow-800',
      Expired: 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const handleViewInvestor = (investor) => {
    setSelectedInvestor(investor);
    setActiveView('details');
  };

  const handleAction = (action) => {
    setShowActionModal(action);
  };

  const confirmAction = () => {
    console.log(`Action: ${showActionModal} for investor ${selectedInvestor.id}`);
    
    // Update investor status based on action
    setInvestors(prevInvestors => 
      prevInvestors.map(investor => {
        if (investor.id === selectedInvestor.id) {
          let newStatus = investor.status;
          let updatedInvestor = { ...investor };
          
          switch (showActionModal) {
            case 'deactivate':
              newStatus = 'Deactivated';
              // Also update wallet status when deactivating
              updatedInvestor.wallets = investor.wallets.map(wallet => ({
                ...wallet,
                status: 'Deactivated'
              }));
              break;
            case 'suspend':
              newStatus = 'Suspended';
              updatedInvestor.wallets = investor.wallets.map(wallet => ({
                ...wallet,
                status: 'Suspended'
              }));
              updatedInvestor.suspensionReason = 'Manually suspended by custodian';
              break;
            case 'activate':
              newStatus = 'Active';
              updatedInvestor.wallets = investor.wallets.map(wallet => ({
                ...wallet,
                status: 'Active'
              }));
              // Remove suspension reason if reactivating
              delete updatedInvestor.suspensionReason;
              break;
            case 'compliance_review':
              // For compliance review, we might want to trigger a review process
              updatedInvestor.lastComplianceReview = new Date().toISOString();
              updatedInvestor.kycStatus = 'Under Review';
              break;
            default:
              break;
          }
          
          return {
            ...updatedInvestor,
            status: newStatus,
            lastActivity: new Date().toISOString()
          };
        }
        return investor;
      })
    );
    
    // Update selected investor to reflect changes
    if (selectedInvestor) {
      let newStatus = selectedInvestor.status;
      let updatedSelectedInvestor = { ...selectedInvestor };
      
      switch (showActionModal) {
        case 'deactivate':
          newStatus = 'Deactivated';
          updatedSelectedInvestor.wallets = selectedInvestor.wallets.map(wallet => ({
            ...wallet,
            status: 'Deactivated'
          }));
          break;
        case 'suspend':
          newStatus = 'Suspended';
          updatedSelectedInvestor.wallets = selectedInvestor.wallets.map(wallet => ({
            ...wallet,
            status: 'Suspended'
          }));
          updatedSelectedInvestor.suspensionReason = 'Manually suspended by custodian';
          break;
        case 'activate':
          newStatus = 'Active';
          updatedSelectedInvestor.wallets = selectedInvestor.wallets.map(wallet => ({
            ...wallet,
            status: 'Active'
          }));
          delete updatedSelectedInvestor.suspensionReason;
          break;
        case 'compliance_review':
          updatedSelectedInvestor.lastComplianceReview = new Date().toISOString();
          updatedSelectedInvestor.kycStatus = 'Under Review';
          break;
        default:
          break;
      }
      
      setSelectedInvestor({
        ...updatedSelectedInvestor,
        status: newStatus,
        lastActivity: new Date().toISOString()
      });
    }
    
    setShowActionModal(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTotalHoldings = (wallets) => {
    let totalValue = 0;
    wallets.forEach(wallet => {
      wallet.holdings.forEach(holding => {
        const value = parseFloat(holding.value.replace(/[^0-9.-]+/g, ''));
        totalValue += value;
      });
    });
    return `MYR ${totalValue.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Link to="/custodian" className="text-gray-400 hover:text-gray-600">
                Custodian Dashboard
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <span className="text-gray-900 font-medium">
                {activeView === 'list' ? 'Investor Management' : `Investor ${selectedInvestor?.id}`}
              </span>
            </div>
            {activeView === 'details' && (
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                ← Back to Investors
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'list' ? (
          <>
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Investor Management</h1>
              <p className="mt-2 text-gray-600">
                Manage approved investors and monitor their compliance status and activities.
              </p>
            </div>

            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Investors
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name, ID, or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Investor Type
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="Individual">Individual</option>
                    <option value="Corporate">Corporate</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Suspended">Suspended</option>
                    <option value="Deactivated">Deactivated</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterType('all');
                      setFilterStatus('all');
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Investors Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Investor
                    </th>
                    <th className="table-header-cell">
                      Type
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Verification Status
                    </th>
                    <th className="table-header-cell">
                      Total Holdings
                    </th>
                    <th className="table-header-cell">
                      Last Activity
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvestors.map((investor) => (
                    <tr key={investor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              {investor.type === 'Individual' ? (
                                <User className="h-5 w-5 text-gray-600" />
                              ) : (
                                <Building2 className="h-5 w-5 text-gray-600" />
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {investor.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {investor.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          investor.type === 'Individual' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {investor.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(investor.status)}`}>
                          {getStatusIcon(investor.status)}
                          <span className="ml-1">{investor.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getKycStatusBadge(investor.kycStatus)}`}>
                          {investor.kycStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {calculateTotalHoldings(investor.wallets)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(investor.lastActivity)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewInvestor(investor)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredInvestors.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No investors found</h3>
                  <p className="text-gray-600">
                    {searchTerm || filterType !== 'all' || filterStatus !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                      : 'No approved investors to display at this time.'
                    }
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Investor Details View */
          selectedInvestor && (
            <div className="space-y-6">
              {/* Investor Header */}
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                          {selectedInvestor.type === 'Individual' ? (
                            <User className="h-6 w-6 text-gray-600" />
                          ) : (
                            <Building2 className="h-6 w-6 text-gray-600" />
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <h1 className="text-2xl font-bold text-gray-900">
                          {selectedInvestor.name}
                        </h1>
                        <p className="text-gray-600">
                          Investor ID: {selectedInvestor.id}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedInvestor.status)}`}>
                      {getStatusIcon(selectedInvestor.status)}
                      <span className="ml-2">{selectedInvestor.status}</span>
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      Approved: {formatDate(selectedInvestor.approvalDate)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Investor Details */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Basic Information */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {selectedInvestor.type === 'Individual' ? 'Personal Information' : 'Company Information'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedInvestor.type === 'Individual' ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Full Name</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedInvestor.name}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">ID Number</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedInvestor.idNumber}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Nationality</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedInvestor.nationality}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Occupation</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedInvestor.occupation}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Company Name</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedInvestor.name}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Registration/Account Number</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedInvestor.registrationNumber}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Business Nature</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedInvestor.businessNature}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">Directors</label>
                            <div className="mt-1 space-y-1">
                              {selectedInvestor.authorizedDirectors?.map((director, index) => (
                                <p key={index} className="text-sm text-gray-900">
                                  {director.name} - {director.designation}
                                </p>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-500">Address</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedInvestor.address}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedInvestor.email}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Phone</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedInvestor.phone}</p>
                      </div>
                    </div>
                  </div>

                  {/* Associated Wallets */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Associated Wallets</h3>
                    <div className="space-y-4">
                      {selectedInvestor.wallets.map((wallet, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Wallet Address: {wallet.address}
                              </p>
                              <p className="text-xs text-gray-500">
                                Created: {formatDate(wallet.createdDate)}
                              </p>
                            </div>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(wallet.status)}`}>
                              {wallet.status}
                            </span>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Token Holdings</h4>
                            <div className="space-y-2">
                              {wallet.holdings.map((holding, holdingIndex) => (
                                <div key={holdingIndex} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{holding.bondName}</p>
                                    <p className="text-xs text-gray-500">{holding.tokens} tokens</p>
                                  </div>
                                  <p className="text-sm font-medium text-gray-900">{holding.value}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Transaction History */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction History</h3>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead className="table-header">
                          <tr>
                            <th className="table-header-cell">
                              Date
                            </th>
                            <th className="table-header-cell">
                              Type
                            </th>
                            <th className="table-header-cell">
                              Bond
                            </th>
                            <th className="table-header-cell">
                              Amount
                            </th>
                            <th className="table-header-cell">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedInvestor.transactions.map((transaction) => (
                            <tr key={transaction.id}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(transaction.date)}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {transaction.type}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {transaction.bond}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {transaction.amount}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {transaction.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Compliance & Actions */}
                <div className="space-y-6">
                  {/* Compliance Information */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance Status</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Verification Status</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getKycStatusBadge(selectedInvestor.kycStatus)}`}>
                          {selectedInvestor.kycStatus}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Risk Rating</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskBadge(selectedInvestor.riskRating)}`}>
                          {selectedInvestor.riskRating} Risk
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Last Compliance Review</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(selectedInvestor.lastComplianceReview)}
                        </p>
                      </div>
                      {selectedInvestor.suspensionReason && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Suspension Reason</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedInvestor.suspensionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Investor Actions</h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => handleAction('compliance_review')}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Trigger Compliance Review
                      </button>
                      
                      {selectedInvestor.status === 'Active' && (
                        <button
                          onClick={() => handleAction('suspend')}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                        >
                          <Pause className="h-4 w-4 mr-2" />
                          Suspend Investor
                        </button>
                      )}
                      
                      {selectedInvestor.status === 'Suspended' && (
                        <button
                          onClick={() => handleAction('activate')}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Activate Investor
                        </button>
                      )}
                      
                      {selectedInvestor.status !== 'Deactivated' && (
                        <button
                          onClick={() => handleAction('deactivate')}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Deactivate Investor
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>

      {/* Action Confirmation Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                {showActionModal === 'compliance_review' && <RefreshCw className="h-6 w-6 text-blue-600" />}
                {showActionModal === 'suspend' && <Pause className="h-6 w-6 text-yellow-600" />}
                {showActionModal === 'activate' && <Play className="h-6 w-6 text-green-600" />}
                {showActionModal === 'deactivate' && <UserX className="h-6 w-6 text-red-600" />}
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {showActionModal === 'compliance_review' && 'Trigger Compliance Review'}
                  {showActionModal === 'suspend' && 'Suspend Investor'}
                  {showActionModal === 'activate' && 'Activate Investor'}
                  {showActionModal === 'deactivate' && 'Deactivate Investor'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {showActionModal === 'compliance_review' && `Initiate a new compliance review for ${selectedInvestor.name}?`}
                  {showActionModal === 'suspend' && `Temporarily suspend ${selectedInvestor.name}'s account and wallet access?`}
                  {showActionModal === 'activate' && `Reactivate ${selectedInvestor.name}'s account and restore wallet access?`}
                  {showActionModal === 'deactivate' && `Permanently deactivate ${selectedInvestor.name}'s account? This action cannot be undone.`}
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowActionModal(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    showActionModal === 'compliance_review' ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' :
                    showActionModal === 'suspend' ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500' :
                    showActionModal === 'activate' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' :
                    'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {showActionModal === 'compliance_review' && 'Start Review'}
                  {showActionModal === 'suspend' && 'Suspend'}
                  {showActionModal === 'activate' && 'Activate'}
                  {showActionModal === 'deactivate' && 'Deactivate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorManagement; 